'use client';

import { FC, PropsWithChildren } from 'react';
import { Logo } from '@puzzlio/ui';
import { cn } from '@/app/lib/utils';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useUser } from '@/app/providers/UserProvider';
import { FlatButton, ArrowLeftIcon } from '@puzzlio/ui';
import { AvatarButton } from '@/app/components/new-ui/atoms/avatar-button';
import { CommentRepliesButton } from '@/app/components/new-ui/molecules/comment-replies-button';

export const PageHeader: FC<
  PropsWithChildren<{
    className?: string;
    withOpacity?: boolean;
    withAccountButton?: boolean;
    backHref?: string;
    /** Back returns to wherever the visitor came from; `backHref` is then only for a tab with no history. */
    backViaHistory?: boolean;
    hideLogoWhenLoggedOut?: boolean;
  }>
> = ({
  className,
  children,
  withOpacity = false,
  withAccountButton = true,
  backHref,
  backViaHistory = false,
  hideLogoWhenLoggedOut = false,
}) => {
  const router = useRouter();
  const email = useUser()?.auth?.email;
  const hideLogo = hideLogoWhenLoggedOut && !email;

  return (
    <div
      className={cn('relative flex w-full items-center px-6 py-3', className)}
      data-ph-capture-attribute-nav="header"
    >
      {withOpacity && <div className="absolute inset-0 -z-10 bg-white opacity-30" />}
      {backHref && (
        <Link
          href={backHref}
          prefetch={false}
          className="flex items-center"
          onClick={(e) => {
            if (backViaHistory && window.history.length > 1) {
              e.preventDefault();
              router.back();
            }
          }}
        >
          <FlatButton size="mobileIcon" asChild className="lg:size-10 lg:border-2 lg:p-2">
            <span>
              <ArrowLeftIcon className="size-5" />
            </span>
          </FlatButton>
        </Link>
      )}
      <Link
        href="/"
        prefetch={false}
        className={cn(
          'flex items-center',
          backHref && 'absolute left-1/2 -translate-x-1/2',
          hideLogo && 'hidden md:flex'
        )}
      >
        <Logo className="h-10" withName={true} />
      </Link>
      {children}
      {withAccountButton && email && (
        <div className="ml-auto flex items-center gap-1.5">
          <CommentRepliesButton />
          <AvatarButton initial={email.at(0)?.toUpperCase() || '?'} />
        </div>
      )}
    </div>
  );
};
